import { create } from 'zustand';
import { apiClient } from '../services/api.js';

const usePlanStore = create((set, get) => ({
  // State
  currentPlan: null,
  loading: false,
  error: null,
  selectedPlanId: '',
  allPlan: [],
  planType: 'Weekly',

  setPlanType: (plan) => {
    set( {planType: plan});
  },

  fetchPlans: async () => {
    const data = await apiClient.get('/api/v1/plans')
    set( { allPlan: data })
  },

  fetchPlan: async (planId) => {
    console.log(`fetch plan for ${planId}`)
    const data = await apiClient.get(`/api/v1/plans/${planId}`)
    set( { currentPlan: data })
  },

  createPlan: async (weekStartDate, weekEndDate, plans, type='Weekly') => {
    try {
      if (type === 'Weekly') {

        const data = await apiClient.post('/api/v1/plans?plan_type=weekly', {
          start_date: weekStartDate,
          end_date: weekEndDate,
          plans: plans
        })
        return data

      } else if (type === 'Event') {

        console.log(`Saving event plan ${plans.title}`)
        const data = await apiClient.post('/api/v1/plans?plan_type=event', {
          start_date: weekStartDate,
          end_date: weekStartDate,
          mode: 'event',
          event_name: plans.title,
          event_date: plans.date,
          guests: plans.guestCount,
          plans: [],
        })
      }
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  randomizePlan: async (startDate, endDate, mealPreferences = null, rotationDays = 14) => {
    set({ loading: true })
    try {
      const requestBody = {
        start_date: startDate,
        end_date: endDate,
        rotation_days: rotationDays
      };
      
      // Add meal preferences if provided
      if (mealPreferences) {
        requestBody.meal_preferences = mealPreferences;
      }
      
      const data = await apiClient.post('/api/v1/plans/randomize', requestBody);
      const transformedData = {
        start_date: startDate,
        end_date: endDate,
        plans: data.plans
      };
      set({ currentPlan: transformedData, loading: false, selectedPlanId: '' });
    } catch (error) {
      set({ error: error.message, loading: false, selectedPlanId: '' });
    }
  },

  selectPlan: (planId) => {
    set({ selectedPlanId: planId });
  },

  addDishToPlan: (day, date, mealTime, dish) => {
    const { currentPlan } = get();

    if (!currentPlan) return;

    // Handle event plans
    if (currentPlan.mode === 'event') {
      const dishItem = {
        name: dish.name,
        description: dish.description || `Delicious ${dish.name}`,
        type: dish.address ? 'restaurant' : 'dish',
        id: dish.id
      };

      const updatedPlan = {
        ...currentPlan,
        items: [...(currentPlan.items || []), dishItem]
      };

      set({ currentPlan: updatedPlan });
      return;
    }

    // Handle weekly plans
    if (!currentPlan.plans) return;

    const dayMap = {
      'Mon': 'Monday',
      'Tue': 'Tuesday',
      'Wed': 'Wednesday',
      'Thu': 'Thursday',
      'Fri': 'Friday',
      'Sat': 'Saturday',
      'Sun': 'Sunday'
    };

    const fullDayName = dayMap[day];
    const mealKey = mealTime.toLowerCase();

    // Check if the day exists in the current plan
    const dayExists = currentPlan.plans.some(dayPlan => dayPlan.day_of_week === fullDayName);

    let updatedPlan;

    if (dayExists) {
      console.log(`update plan day in ${day}-${mealTime}`)
      updatedPlan = {
        ...currentPlan,
        plans: currentPlan.plans.map(dayPlan => {
          if (dayPlan.day_of_week === fullDayName) {
            return {
              ...dayPlan,
              [mealKey]: [
                ...(dayPlan[mealKey] || []),
                {
                  name: dish.name,
                  description: dish.description || `Delicious ${dish.name}`,
                  type: dish.address ? 'restaurant' : 'dish',
                  id: dish.id
                }
              ]
            };
          }
          return dayPlan;
        })
      };
    } else {
      // Day doesn't exist, create new day with the dish
      console.log(`add new plan day in ${day}-${mealTime}`)
      const newDayPlan = {
        day_of_week: fullDayName,
        date: date,
        breakfast: [],
        lunch: [],
        dinner: [],
        [mealKey]: [{
          name: dish.name,
          description: dish.description || `Delicious ${dish.name}`,
          type: dish.address ? 'restaurant' : 'dish',
          id: dish.id
        }]
      };
      console.log(newDayPlan);

      updatedPlan = {
        ...currentPlan,
        plans: [...currentPlan.plans, newDayPlan]
      };
    }

    set({ currentPlan: updatedPlan });
  },

  deleteDishFromPlan: (day, mealTime, dishIndex) => {
    const { currentPlan } = get();
    console.log(`Delete ${dishIndex} from ${day}-${mealTime}`)

    if (!currentPlan || !currentPlan.plans) return;

    const dayMap = {
      'Mon': 'Monday',
      'Tue': 'Tuesday',
      'Wed': 'Wednesday',
      'Thu': 'Thursday',
      'Fri': 'Friday',
      'Sat': 'Saturday',
      'Sun': 'Sunday'
    };

    const fullDayName = dayMap[day];
    const mealKey = mealTime.toLowerCase();

    const updatedPlan = {
      ...currentPlan,
      plans: currentPlan.plans.map(dayPlan => {
        if (dayPlan.day_of_week === fullDayName) {
          return {
            ...dayPlan,
            [mealKey]: dayPlan[mealKey].filter((_, index) => index !== dishIndex)
          };
        }
        return dayPlan;
      })
    };

    set({ currentPlan: updatedPlan });
  },

  deleteDishFromEventPlan: (dishIndex) => {
    const { currentPlan } = get();
    console.log(`Delete dish at index ${dishIndex} from event plan`);

    if (!currentPlan || currentPlan.mode !== 'event' || !currentPlan.items) return;

    const updatedPlan = {
      ...currentPlan,
      items: currentPlan.items.filter((_, index) => index !== dishIndex)
    };

    set({ currentPlan: updatedPlan });
  },

  moveDishInPlan: (fromDay, fromMeal, toDay, toMeal, dishIndex) => {
    const { currentPlan } = get();

    if (!currentPlan || !currentPlan.plans) return;

    const dayMap = {
      'Mon': 'Monday',
      'Tue': 'Tuesday',
      'Wed': 'Wednesday',
      'Thu': 'Thursday',
      'Fri': 'Friday',
      'Sat': 'Saturday',
      'Sun': 'Sunday'
    };

    const fromFullDayName = dayMap[fromDay];
    const toFullDayName = dayMap[toDay];
    const fromMealKey = fromMeal.toLowerCase();
    const toMealKey = toMeal.toLowerCase();

    // Find the dish to move
    let dishToMove = null;
    const updatedPlan = {
      ...currentPlan,
      plans: currentPlan.plans.map(dayPlan => {
        if (dayPlan.day_of_week === fromFullDayName) {
          dishToMove = dayPlan[fromMealKey][dishIndex];
          return {
            ...dayPlan,
            [fromMealKey]: dayPlan[fromMealKey].filter((_, index) => index !== dishIndex)
          };
        }
        return dayPlan;
      })
    };

    // Add the dish to the target location
    if (dishToMove) {
      const finalPlan = {
        ...updatedPlan,
        plans: updatedPlan.plans.map(dayPlan => {
          if (dayPlan.day_of_week === toFullDayName) {
            return {
              ...dayPlan,
              [toMealKey]: [...dayPlan[toMealKey], dishToMove]
            };
          }
          return dayPlan;
        })
      };

      set({ currentPlan: finalPlan });
    }
  },

  updatePlanDay: async (planId, startWeek, endWeek, plans) => {
    try {
      console.log(`${startWeek} - ${endWeek}`)
      const data = await apiClient.put(`/api/v1/plans/${planId}`, {
        "start_date": startWeek,
        "end_date": endWeek,
        "plans": plans,
        // "date": date,
        // "meal": meal,
        // "dish_ids": dishIds,
        // "dining_out": diningOut,
      })
      return data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  updateEventPlan: async (planId, eventData) => {
    try {
      console.log(`Updating event plan ${planId}:`, eventData)
      const data = await apiClient.put(`/api/v1/plans/${planId}`, {
        start_date: eventData.event_date,
        end_date: eventData.event_date,
        mode: 'event',
        event_name: eventData.event_name,
        event_date: eventData.event_date,
        guests: eventData.guests,
        description: eventData.description,
        plans: eventData.plans
      })
      return data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  deletePlan: async (planId) => {
    try {
      const data = await apiClient.delete(`/api/v1/plans/${planId}`);
      return { success: true };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

}));

export default usePlanStore;